/**
 * One row of the desktop queue table: age, reason, reference, workshop, money,
 * action.
 *
 * The row itself is NOT clickable. Opening the review is the one primary button;
 * the reference and the workshop are their own links. A `<tr onClick>` wrapping
 * inner links is ambiguous about what was hit, and is neither focusable nor
 * announced without re-implementing what a real button already gives us.
 */
import { workshopDetailUrlFor, type WorkshopShell } from '../../dashboards/workshopDetailUrlFor';
import {
  ageLabel,
  customerLabel,
  extraReasonCount,
  formatInstant,
  formatMoney,
  purchaseDetailLine,
  purchaseLabel,
  reasonClauses,
  reasonText,
} from '../reviewQueueModel';
import {
  inkFor,
  MUTED_INK,
  paidLine,
  paidTone,
  REASON_CLAMP_STYLE,
  SUBTLE,
} from '../reviewQueueRowStyle';
import { ReviewRowLink } from '../ReviewRowLink';
import type { ReviewQueueItem } from '../reviewTypes';

import ReviewQueueActionCell from './ReviewQueueActionCell';

export default function ReviewQueueRow({
  item,
  shell,
  muted,
}: {
  item: ReviewQueueItem;
  shell: WorkshopShell;
  muted: boolean;
}) {
  const reason = reasonText(item.reviewReason);
  const clauses = reasonClauses(item.reviewReason);
  const extras = extraReasonCount(item.reviewReason);
  // A flagged checkout has no booking to open — none was ever created.
  const bookingHref = item.kind === 'booking' ? `/admin/bookings/${item.id}` : null;
  const workshopHref = item.workshop?.id ? workshopDetailUrlFor(shell, item.workshop.id) : null;

  return (
    <tr data-resolved={muted ? 'true' : 'false'}>
      <td style={{ paddingLeft: 18, whiteSpace: 'nowrap' }}>
        <span
          className="nb-tabular"
          style={{ fontSize: 12.5, color: 'var(--nb-muted)' }}
          title={formatInstant(item.flaggedAt)}
        >
          {ageLabel(item.ageDays)}
        </span>
      </td>
      <td style={{ maxWidth: 300 }}>
        <span
          style={muted ? { ...REASON_CLAMP_STYLE, color: MUTED_INK } : REASON_CLAMP_STYLE}
          title={reason}
        >
          {clauses[0]}
        </span>
        {/* U5: the payment path joins several reasons with "; ", and a two-line
            clamp showed the first and gave NO sign the rest existed — the full
            string was only on the `title`, which is mouse-only. */}
        {extras > 0 && (
          <span style={{ ...SUBTLE, display: 'block' }} title={reason}>
            +{extras} more {extras === 1 ? 'reason' : 'reasons'}
          </span>
        )}
      </td>
      <td>
        <div style={{ fontWeight: 600, fontSize: 13, color: inkFor(muted) }}>
          {customerLabel(item.customer)}
        </div>
        <div className="nb-mono" style={SUBTLE}>
          <ReviewRowLink to={bookingHref}>{item.reference}</ReviewRowLink>
        </div>
      </td>
      <td>
        <div style={{ fontSize: 12.5, color: inkFor(muted) }}>
          <ReviewRowLink to={workshopHref}>{purchaseLabel(item.workshop)}</ReviewRowLink>
        </div>
        <div style={SUBTLE}>{purchaseDetailLine(item)}</div>
      </td>
      <td
        className="nb-tabular"
        style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, color: inkFor(muted) }}
      >
        {formatMoney(item.totalCents, item.currency)}
        {/* U4: `amountPaidCents` was on the wire and unrendered, so the queue
            showed what was OWED and never what was TAKEN — on a surface whose
            whole subject is payments that went wrong. A mismatch is the single
            most triage-relevant fact a row can carry, so it is tinted. A checkout
            has no booking to hold the figure, which is itself worth saying. */}
        <div style={{ ...SUBTLE, fontWeight: 600, color: paidTone(item, muted) }}>
          {paidLine(item)}
        </div>
      </td>
      <td style={{ paddingRight: 18 }}>
        <ReviewQueueActionCell item={item} muted={muted} />
      </td>
    </tr>
  );
}
